import nluApi from '../../api/nlu'
import _ from 'lodash'

const BULK_SIZE = 10

export default {
  namespaced: true,

  state: {
    suggested: {
      loading: true,
      value: [],
      last: null,
      total: null,
      text: ''
    },
    unrecognized: {
      loading: true,
      value: [],
      last: null,
      total: null,
      text: ''
    }
  },

  actions: {
    async fetchAutogenerated ({ state, rootGetters, commit, dispatch }, { type, firstLoad = false, searchText = '' }) {
      try {
        const { value, last, total, text } = _.get(state, `${type}`)
        if (total === value.length && searchText === text && !firstLoad) {
          return
        }
        if (!firstLoad) {
          firstLoad = searchText !== text
        }
        commit('SET_AUTOGENERATED', {
          type,
          loading: true,
          value: firstLoad ? [] : value,
          last: firstLoad ? null : last,
          total: firstLoad ? null : total,
          text: searchText
        })
        const response = await nluApi.listUtterances({
          kbId: rootGetters['knowledgebase/getCurrent'].id,
          last: _.get(state, `${type}.last`),
          autoGenerated: true,
          unrecognized: type === 'unrecognized',
          limit: BULK_SIZE,
          text: searchText
        })
        commit('SET_AUTOGENERATED', {
          type,
          value: firstLoad
            ? response.items
            : [..._.get(state, `${type}.value`), ...response.items],
          last: _.has(response, 'last')
            ? response.last
            : _.get(state, `${type}.last`),
          total: firstLoad
            ? response.total.value
            : _.get(state, `${type}.total`),
          text: searchText
        })

        return response
      } catch (error) {
        const { value, last, total, text } = _.get(state, `${type}`)
        commit('SET_AUTOGENERATED', { type, value, last, total, text })
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: `store.autogenerated.fetchAutogenerated.errorNotification.${type}`, error },
          { root: true }
        )
      }
    },

    async updateAutogenerated ({ state, rootState, rootGetters, commit, dispatch }, { appId, data }) {
      let type
      try {
        type = data.unrecognized ? 'unrecognized' : 'suggested'
        const utteranceAllowedOptions = [
          'id', 'autoGenerated', 'unrecognized', 'text', 'intent', 'dateCreated',
          'intentCreated', 'appCreated', 'entityPredictions', 'entities'
        ]
        const response = await nluApi.updateUtterance({
          appId,
          kbId: rootState.knowledgebase.current,
          utterance: _.pick(data, utteranceAllowedOptions)
        })
        const { value, last, total } = _.get(state, `${type}`)
        const newValue = _.map(value, obj => obj.id === data.id ? { ...obj, ...response } : obj)
        commit('SET_AUTOGENERATED', { type, value: newValue, last, total })

        return response
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: `store.autogenerated.updateAutogenerated.errorNotification.${type}`, error },
          { root: true }
        )

        return Promise.resolve()
      }
    },

    async batchDeleteAutogenerated ({ state, rootState, rootGetters, commit, dispatch }, { ids, type, searchText }) {
      try {
        await nluApi.batchDeleteUtterances({
          ids,
          kbId: rootState.knowledgebase.current
        })
        const { value, last, total } = _.get(state, `${type}`)
        const newValue = _.filter(value, obj => !_.includes(ids, obj.id))
        commit('SET_AUTOGENERATED', {
          type,
          value: newValue,
          total: total - ids.length,
          text: searchText,
          last
        })

        return Promise.resolve()
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.autogenerated.batchDeleteAutogenerated.errorNotification.title', error },
          { root: true }
        )
      }
    },

    async batchApproveAutogenerated ({ state, rootState, rootGetters, commit, dispatch }, { type, utterances, searchText }) {
      try {
        const ids = _.map(utterances, 'id')
        utterances = {
          utterances: _.map(utterances, obj => {
            const parsedIntent = JSON.parse(obj.intent)
            return _.pick({
              ...obj,
              intent: parsedIntent.name,
              intentType: parsedIntent.type,
              autoGenerated: false,
              unrecognized: false
            }, [
              'kbId',
              'id',
              'autoGenerated',
              'unrecognized',
              'text',
              'entities',
              'entityPredictions',
              'intent',
              'intentType',
              'application',
              'versionId',
              'dateCreated',
              'intentCreated',
              'appCreated'
            ])
          })
        }
        const response = await nluApi.batchUpdatePairs({
          kbId: rootState.knowledgebase.current,
          utterances
        })
        const failedIds = !_.isEmpty(response.failed) ? _.map(response.failed, obj => _.get(obj, 'utterance.id')) : []
        const { value, last, total } = _.get(state, `${type}`)
        const newValue = _.filter(value, obj => !_.includes(_.filter(ids, id => !_.includes(failedIds, id)), obj.id))
        commit('SET_AUTOGENERATED', {
          type,
          value: newValue,
          total: total - ids.length + failedIds.length,
          last,
          text: searchText
        })
        if (!_.isEmpty(failedIds)) {
          dispatch('knowledgebase/notifications/addWarning',
            { titleLabel: 'store.autogenerated.batchApproveAutogenerated.warningNotification.title' },
            { root: true }
          )
        }

        return Promise.resolve()
      } catch (error) {
        dispatch('knowledgebase/notifications/addError',
          { titleLabel: 'store.autogenerated.batchApproveAutogenerated.errorNotification.title', error },
          { root: true }
        )
      }
    }
  },

  mutations: {
    SET_AUTOGENERATED (state, { type, loading = false, value, last, total, text = '' }) {
      _.set(state, `${type}`, { loading, value, last, total, text })
    }
  }
}
